<script setup lang="ts">
/**
 * Navigation icon component — renders the user's PNG icons but masked so
 * they inherit the current `color` (works in both light/dark mode AND in
 * the active link state without needing duplicate assets).
 *
 * Implementation : a `<span>` whose `background-color` is `currentColor`
 * and whose mask is the icon PNG. The PNG must have transparent
 * background and opaque black shape (already prepared in `public/icons/`).
 *
 * Sizes via CSS variable so the same component can be used at different
 * sizes (16px in nav text, 22px in mobile nav, 28px in app header, etc).
 */
defineProps<{
  /** Which icon to render. Maps to `/icons/{name}.png`. */
  name: "quotes" | "glass" | "prices" | "settings";
  /** Pixel size override. Default 18. */
  size?: number;
}>();
</script>

<template>
  <span
    class="nav-icon"
    :class="`icon-${name}`"
    :style="{
      width: (size || 18) + 'px',
      height: (size || 18) + 'px',
      maskImage: `url(/icons/${name}.png)`,
      WebkitMaskImage: `url(/icons/${name}.png)`,
    }"
    aria-hidden="true"
  />
</template>

<style scoped>
.nav-icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
  /* Color the mask using the surrounding text color */
  background-color: currentColor;
  /* CSS mask : the PNG defines which parts of the colored background show through */
  mask-repeat: no-repeat;
  mask-size: contain;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  -webkit-mask-position: center;
}
</style>
